import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import type {
  EventQueryGroupBy,
  EventQueryInclude,
  EventQueryInterval,
  EventQueryOrderBy,
  EventQueryResult,
} from "@/analytics/types";

import type { DateRangeWire } from "@/api/analytics";

import type {
  PreviewDevice,
  PreviewSession,
  PreviewSessionEvent,
} from "./types";

// Event names that represent conversions when filter.eventKind === "conversion".
const CONVERSION_EVENT_NAMES = new Set([
  "form_submitted",
  "appointment_booking_completed",
  "call_initiated",
]);

// Events counted for filter.eventKind === "precision" — anything custom
// (everything except auto-tracked page_view).
function isPrecisionEvent(name: string): boolean {
  return name !== "page_view";
}

// Parse a wire-shape DateRange (date_from / date_to may be ISO timestamps OR
// relative expressions like "-7d", "0dStart") into [start, end] timestamps.
// `now` is the reference point — for preview mode, this is graph.windowEnd.
const RELATIVE_DATE_RE = /^(-?[0-9]+)?([hdwmy])(Start|End)?$/;

function parseRelative(expr: string, now: number): number {
  const match = RELATIVE_DATE_RE.exec(expr);
  if (!match) return Number.NaN;
  const [, numStr, unit, position] = match;
  const num = numStr === undefined || numStr === "" ? 0 : parseInt(numStr, 10);
  const base = new Date(now);
  let shifted: Date;
  switch (unit) {
    case "h":
      shifted = addHours(base, num);
      break;
    case "d":
      shifted = addDays(base, num);
      break;
    case "w":
      shifted = addWeeks(base, num);
      break;
    case "m":
      shifted = addMonths(base, num);
      break;
    case "y":
      shifted = addYears(base, num);
      break;
    default:
      return Number.NaN;
  }
  if (position === "Start") {
    return startOfDay(shifted).getTime();
  }
  if (position === "End") {
    // End-of-day: 23:59:59.999
    return startOfDay(addDays(shifted, 1)).getTime() - 1;
  }
  return shifted.getTime();
}

function parseDateField(raw: string, now: number): number {
  if (RELATIVE_DATE_RE.test(raw)) return parseRelative(raw, now);
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? now : t;
}

export function parseWireDateRange(
  dateRange: DateRangeWire,
  now: number,
): { start: number; end: number } {
  const start = parseDateField(dateRange.date_from, now);
  const end =
    dateRange.date_to !== undefined
      ? parseDateField(dateRange.date_to, now)
      : now;
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

// --- Time bucketing ---

function bucketStart(timestamp: number, interval: EventQueryInterval): number {
  const d = new Date(timestamp);
  switch (interval) {
    case "hour":
      return startOfHour(d).getTime();
    case "day":
      return startOfDay(d).getTime();
    case "week":
      return startOfWeek(d, { weekStartsOn: 0 }).getTime();
    case "month":
      return startOfMonth(d).getTime();
  }
}

export function enumerateBuckets(
  start: number,
  end: number,
  interval: EventQueryInterval,
): number[] {
  const buckets: number[] = [];
  let cursor = bucketStart(start, interval);
  while (cursor <= end) {
    buckets.push(cursor);
    const d = new Date(cursor);
    switch (interval) {
      case "hour":
        cursor = addHours(d, 1).getTime();
        break;
      case "day":
        cursor = addDays(d, 1).getTime();
        break;
      case "week":
        cursor = addWeeks(d, 1).getTime();
        break;
      case "month":
        cursor = addMonths(d, 1).getTime();
        break;
    }
  }
  return buckets;
}

// --- User-agent parsing for browser/os/device_type grouping ---

export function browserFromUA(ua: string): string {
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
  if (ua.includes("Firefox/")) return "Firefox";
  return "Other";
}

export function osFromUA(ua: string): string {
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS X") && !ua.includes("iPhone") && !ua.includes("iPad"))
    return "macOS";
  if (ua.includes("iPhone")) return "iOS";
  if (ua.includes("iPad")) return "iPadOS";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("Linux")) return "Linux";
  return "Other";
}

export function deviceTypeFromUA(ua: string): string {
  if (ua.includes("iPad")) return "Tablet";
  if (ua.includes("iPhone") || ua.includes("Android")) return "Mobile";
  return "Desktop";
}

// --- Referring domain derivation (loose mapping from utm.source) ---

function referringDomainFor(session: PreviewSession): string | null {
  // The SDK reports "$direct" for visits with no resolvable referrer; the
  // backend passes that value through to the analytics response unchanged.
  if (!session.utm) return "$direct";
  switch (session.utm.source) {
    case "google":
      return "google.com";
    case "bing":
      return "bing.com";
    case "meta":
    case "facebook":
      return "facebook.com";
    case "instagram":
      return "instagram.com";
    case "email":
      return "newsletter";
    default:
      return session.utm.source;
  }
}

// --- Group-key extraction ---

type DeviceLookup = (deviceID: string) => PreviewDevice | undefined;

export function groupKeyFor(
  groupBy: EventQueryGroupBy,
  session: PreviewSession,
  event: PreviewSessionEvent,
  device: PreviewDevice | undefined,
): string | null {
  switch (groupBy) {
    case "all":
      return "all";
    case "event":
      return event.eventName;
    case "path":
      return event.path;
    case "region":
      return device?.region ?? null;
    case "city":
      return device?.city ?? null;
    case "county":
      return device?.county ?? null;
    case "zip":
      return device?.zipCode ?? null;
    case "channel":
      return session.channel;
    case "referring_domain":
      return referringDomainFor(session);
    case "utm_source":
      return session.utm?.source ?? null;
    case "utm_medium":
      return session.utm?.medium ?? null;
    case "utm_campaign":
      return session.utm?.campaign ?? null;
    case "utm_term":
    case "utm_content":
      return null;
    case "os":
      return device ? osFromUA(device.userAgent) : null;
    case "browser":
      return device ? browserFromUA(device.userAgent) : null;
    case "device_type":
      return device ? deviceTypeFromUA(device.userAgent) : null;
  }
}

// --- Aggregation ---

interface AggBucket {
  events: number;
  // Tracked as Sets; resolved to counts on serialization.
  sessionIDs: Set<string>;
  visitorIDs: Set<string>;
  // Per-session contributions for duration / bounce, keyed by sessionID so
  // each session is counted once per group.
  durationsMs: Map<string, number>;
  pageViewsBySession: Map<string, number>;
}

function emptyBucket(): AggBucket {
  return {
    events: 0,
    sessionIDs: new Set(),
    visitorIDs: new Set(),
    durationsMs: new Map(),
    pageViewsBySession: new Map(),
  };
}

function eventPassesKindFilter(
  eventName: string,
  kind: "conversion" | "pageview" | "precision" | undefined,
): boolean {
  if (!kind) return true;
  if (kind === "pageview") return eventName === "page_view";
  if (kind === "conversion") return CONVERSION_EVENT_NAMES.has(eventName);
  if (kind === "precision") return isPrecisionEvent(eventName);
  return true;
}

interface AggregateArgs {
  sessions: readonly PreviewSession[];
  deviceLookup: DeviceLookup;
  windowStart: number;
  windowEnd: number;
  groupBy: EventQueryGroupBy;
  filterEventKind?: "conversion" | "pageview" | "precision";
  interval?: EventQueryInterval;
}

// Compute aggregates once per (group, bucket?). The output is a flat list of
// (key, bucket, agg) tuples ready for serialization.
function aggregate(
  args: AggregateArgs,
): Array<{ key: string; bucket: number | null; agg: AggBucket }> {
  const { sessions, deviceLookup, windowStart, windowEnd, groupBy, interval } =
    args;
  const buckets = new Map<string, Map<number | null, AggBucket>>();

  const bucketKey = (ts: number): number | null =>
    interval ? bucketStart(ts, interval) : null;

  // For session-level metrics (duration, pageViewsBySession) we need to know
  // each session's full event list within the window before assigning to
  // groups. Pre-compute session totals.
  const sessionDurationByID = new Map<string, number>();
  const sessionPageViewsByID = new Map<string, number>();
  for (const session of sessions) {
    if (session.startedAt < windowStart || session.startedAt > windowEnd)
      continue;
    let lastTs = session.startedAt;
    let pageViews = 0;
    for (const evt of session.events) {
      if (evt.timestamp > windowEnd) break;
      lastTs = evt.timestamp;
      if (evt.eventName === "page_view") pageViews++;
    }
    sessionDurationByID.set(session.sessionID, lastTs - session.startedAt);
    sessionPageViewsByID.set(session.sessionID, pageViews);
  }

  for (const session of sessions) {
    if (session.startedAt < windowStart || session.startedAt > windowEnd)
      continue;
    const device = deviceLookup(session.deviceID);
    for (const evt of session.events) {
      if (evt.timestamp < windowStart || evt.timestamp > windowEnd) continue;
      if (!eventPassesKindFilter(evt.eventName, args.filterEventKind)) continue;
      const key = groupKeyFor(groupBy, session, evt, device);
      if (key === null) continue;
      const bucket = bucketKey(evt.timestamp);
      let byBucket = buckets.get(key);
      if (!byBucket) {
        byBucket = new Map();
        buckets.set(key, byBucket);
      }
      let agg = byBucket.get(bucket);
      if (!agg) {
        agg = emptyBucket();
        byBucket.set(bucket, agg);
      }
      agg.events++;
      agg.sessionIDs.add(session.sessionID);
      agg.visitorIDs.add(session.deviceID);
      if (!agg.durationsMs.has(session.sessionID)) {
        agg.durationsMs.set(
          session.sessionID,
          sessionDurationByID.get(session.sessionID) ?? 0,
        );
      }
      if (!agg.pageViewsBySession.has(session.sessionID)) {
        agg.pageViewsBySession.set(
          session.sessionID,
          sessionPageViewsByID.get(session.sessionID) ?? 0,
        );
      }
    }
  }

  const out: Array<{ key: string; bucket: number | null; agg: AggBucket }> = [];
  for (const [key, byBucket] of buckets) {
    for (const [bucket, agg] of byBucket) {
      out.push({ key, bucket, agg });
    }
  }
  return out;
}

// --- Serialization ---

function aggToResult(
  key: string,
  bucket: number | null,
  agg: AggBucket,
  include: ReadonlySet<EventQueryInclude>,
  groupBy: EventQueryGroupBy,
  series: "current" | "previous" | null,
): EventQueryResult {
  const result: EventQueryResult = {};
  if (groupBy !== "all") result.label = key;
  if (bucket !== null) result.timeBucket = new Date(bucket).toISOString();
  if (series !== null) result.series = series;
  if (include.has("events")) result.events = agg.events;
  if (include.has("visitors")) result.visitors = agg.visitorIDs.size;
  if (include.has("sessions")) result.sessions = agg.sessionIDs.size;
  if (include.has("session_duration_ms")) {
    const total = Array.from(agg.durationsMs.values()).reduce(
      (s, v) => s + v,
      0,
    );
    result.sessionDurationMs =
      agg.durationsMs.size === 0 ? 0 : Math.round(total / agg.durationsMs.size);
  }
  if (include.has("bounce_rate_pct")) {
    const counts = Array.from(agg.pageViewsBySession.values());
    const bounced = counts.filter((c) => c <= 1).length;
    result.bounceRatePct =
      counts.length === 0 ? 0 : (bounced / counts.length) * 100;
  }
  return result;
}

// --- Public entry point ---

export interface RunAnalyticsEventQueryArgs {
  sessions: readonly PreviewSession[];
  previousSessions: readonly PreviewSession[];
  devices: readonly PreviewDevice[];
  windowEnd: number;
  request: {
    compare: boolean;
    dateRange: DateRangeWire;
    filter?: { eventKind?: "conversion" | "pageview" | "precision" };
    groupBy: EventQueryGroupBy;
    order?: { by: EventQueryOrderBy; dir: "asc" | "desc" };
    include: EventQueryInclude[];
    interval?: EventQueryInterval;
    limit?: number;
    offset?: number;
  };
}

export function runAnalyticsEventQuery({
  sessions,
  previousSessions,
  devices,
  windowEnd,
  request,
}: RunAnalyticsEventQueryArgs): EventQueryResult[] {
  const { start, end } = parseWireDateRange(request.dateRange, windowEnd);
  const include = new Set(request.include);
  const deviceMap = new Map(devices.map((d) => [d.deviceID, d]));
  const deviceLookup: DeviceLookup = (id) => deviceMap.get(id);

  const buildSeries = (
    sessionsForSeries: readonly PreviewSession[],
    s: number,
    e: number,
    series: "current" | "previous" | null,
  ): EventQueryResult[] => {
    const aggs = aggregate({
      sessions: sessionsForSeries,
      deviceLookup,
      windowStart: s,
      windowEnd: e,
      groupBy: request.groupBy,
      filterEventKind: request.filter?.eventKind,
      interval: request.interval,
    });

    let rows = aggs.map(({ key, bucket, agg }) =>
      aggToResult(key, bucket, agg, include, request.groupBy, series),
    );

    // For "all" + interval, fill in zero rows for empty buckets so the
    // chart shows a continuous timeline.
    if (request.groupBy === "all" && request.interval) {
      const expected = enumerateBuckets(s, e, request.interval);
      const have = new Set(
        rows
          .map((r) => (r.timeBucket ? new Date(r.timeBucket).getTime() : null))
          .filter((t): t is number => t !== null),
      );
      for (const t of expected) {
        if (!have.has(t)) {
          const empty: EventQueryResult = {
            timeBucket: new Date(t).toISOString(),
          };
          if (series !== null) empty.series = series;
          if (include.has("events")) empty.events = 0;
          if (include.has("visitors")) empty.visitors = 0;
          if (include.has("sessions")) empty.sessions = 0;
          if (include.has("session_duration_ms")) empty.sessionDurationMs = 0;
          if (include.has("bounce_rate_pct")) empty.bounceRatePct = 0;
          rows.push(empty);
        }
      }
      rows.sort(
        (a, b) =>
          new Date(a.timeBucket ?? 0).getTime() -
          new Date(b.timeBucket ?? 0).getTime(),
      );
    }

    return rows;
  };

  const seriesTag: "current" | null = request.compare ? "current" : null;
  let rows = buildSeries(sessions, start, end, seriesTag);

  if (request.compare) {
    // Aggregate the previous period from a fully synthesized session set
    // (PreviewGraph.previousSessions) so deltas reflect real variance in
    // outcomes/channels/paths rather than a flat scale of the current window.
    const span = end - start;
    const prevEnd = start - 1;
    const prevStart = prevEnd - span;
    rows = rows.concat(
      buildSeries(previousSessions, prevStart, prevEnd, "previous"),
    );
  }

  // Apply ordering (only relevant when groupBy != "all" and no interval).
  if (
    request.order &&
    request.groupBy !== "all" &&
    request.interval === undefined
  ) {
    const { by, dir } = request.order;
    const sortKey = (r: EventQueryResult): number | string => {
      switch (by) {
        case "label":
          return r.label ?? "";
        case "events":
          return r.events ?? 0;
        case "visitors":
          return r.visitors ?? 0;
        case "sessions":
          return r.sessions ?? 0;
        case "session_duration_ms":
          return r.sessionDurationMs ?? 0;
        case "bounce_rate_pct":
          return r.bounceRatePct ?? 0;
      }
    };
    rows.sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : (av as number) - (bv as number);
      return dir === "asc" ? cmp : -cmp;
    });
  } else if (request.groupBy !== "all" && request.interval === undefined) {
    // Default sort by visitors desc when caller didn't specify.
    rows.sort((a, b) => (b.visitors ?? 0) - (a.visitors ?? 0));
  }

  // Apply offset/limit (skip for "all" groupBy and time-series).
  if (
    request.groupBy !== "all" &&
    request.interval === undefined &&
    !request.compare
  ) {
    const offset = request.offset ?? 0;
    const limit = request.limit ?? 10;
    rows = rows.slice(offset, offset + limit);
  }

  return rows;
}
